import {supabase} from '../api/supabase';

interface Creator {
  gender: string;
  orientation: string;
}

interface Participant {
  gender: string;
  preference: string;
  orientation: string;
}

/**
 * Validates if a participant can join a room based on gender preference and sexual orientation rules
 * @param creatorId The ID of the room creator
 * @param participantId The ID of the participant trying to join
 * @returns An object with the validation result and a possible rejection message
 */
export const validateRoomJoin = async (
  creatorId: string,
  participantId: string,
): Promise<{isValid: boolean; message?: string}> => {
  try {
    // Fetch creator profile
    const {data: creatorData, error: creatorError} = await supabase
      .from('profiles')
      .select('gender, sexual_orientation')
      .eq('id', creatorId)
      .single();

    if (creatorError || !creatorData) {
      return {
        isValid: false,
        message: 'Could not validate room - creator profile not found',
      };
    }

    // Fetch participant profile
    const {data: participantData, error: participantError} = await supabase
      .from('profiles')
      .select('gender, preference, sexual_orientation')
      .eq('id', participantId)
      .single();

    if (participantError || !participantData) {
      return {
        isValid: false,
        message: 'Could not validate room - participant profile not found',
      };
    }

    const creator: Creator = {
      gender: creatorData.gender,
      orientation: creatorData.sexual_orientation,
    };

    const participant: Participant = {
      gender: participantData.gender,
      preference: participantData.preference,
      orientation: participantData.sexual_orientation,
    };

    // Helper functions for validation results
    const rejectJoin = (message: string) => ({isValid: false, message});
    const allowJoin = () => ({isValid: true});

    // STEP 1: Participant gender preference filter
    if (participant.preference === 'men' && creator.gender !== 'male') {
      return rejectJoin(
        'You prefer men, but this room was created by someone who is not male.',
      );
    }

    if (participant.preference === 'women' && creator.gender !== 'female') {
      return rejectJoin(
        'You prefer women, but this room was created by someone who is not female.',
      );
    }

    if (
      participant.preference === 'both' &&
      !['male', 'female'].includes(creator.gender)
    ) {
      return rejectJoin(
        'You prefer men or women, but this room was created by someone who is neither.',
      );
    }

    // STEP 2: Creator's orientation access rules
    switch (creator.orientation) {
      case 'straight':
        if (creator.gender === 'male' && participant.gender !== 'female') {
          return rejectJoin(
            'This room was created by a straight male who is only interested in female participants.',
          );
        }
        if (creator.gender === 'female' && participant.gender !== 'male') {
          return rejectJoin(
            'This room was created by a straight female who is only interested in male participants.',
          );
        }
        break;

      case 'gay':
        if (creator.gender === 'male') {
          if (
            participant.gender !== 'male' ||
            participant.orientation === 'straight'
          ) {
            return rejectJoin(
              'This room was created by a gay male who is only interested in non-straight male participants.',
            );
          }
        }
        break;

      case 'lesbian':
        if (creator.gender === 'female') {
          if (
            participant.gender !== 'female' ||
            participant.orientation === 'straight'
          ) {
            return rejectJoin(
              'This room was created by a lesbian who is only interested in non-straight female participants.',
            );
          }
        }
        break;

      case 'bisexual':
      case 'pansexual':
        // No restrictions — inclusive of all genders and orientations
        break;

      case 'asexual':
      case 'queer':
      case 'other':
      case 'prefer not to say':
        // Default: allow anyone
        break;

      default:
        return rejectJoin('Unknown or unsupported orientation.');
    }

    return allowJoin();
  } catch (error) {
    return {
      isValid: false,
      message: 'An error occurred during room validation',
    };
  }
};
